import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { config } from '@terminal/config/app.config';
import { TeamsGuard } from '@terminal/config/TeamsGuard';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { SectionBase } from '@terminal/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import type {
  SharedTableColumn,
  SharedTableDataItem,
} from '@terminal/modules/overview-knowledge-graph/components/shared/shared-table/SharedTable';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { TabsType } from '@terminal/modules/tab-content/types';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useCompanyCompetitorWithThemes } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { useCompetitorsDrawer } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/useCompetitorsDrawer';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ChangeCell } from './ChangeCell';
import { CompetitorCatalystsCell } from './CompetitorCatalystsCell';
import { CompetitorScenariosCell } from './CompetitorScenariosCell';
import styles from './CompetitorsSection.module.scss';

type CompetitorTableRow = Entity & {
  priceKey?: string;
  catalystsKey?: string;
  scenariosKey?: string;
};

export interface CompetitorsSectionProps {
  entityTag: string;
  source?: PosthogTrackingSource;
  isInDrawer?: boolean;
}

/**
 * @deprecated
 */
export const CompetitorsSection = ({
  entityTag,
  source,
  isInDrawer = false,
}: CompetitorsSectionProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const catalystSource = source ?? POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW;

  const { openDrawer } = useCompetitorsDrawer();

  const {
    data = [],
    isSuccess,
    isError,
    isFetching,
    error,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    limit: 3,
  });
  const isCompetitorsNotFound = error?.status === httpCode.notFound;
  const entityTags = data.map(item => item.company);

  const {
    data: entitiesData = [],
    isError: isEntitiesError,
    isLoading: isEntitiesLoading,
  } = useEntities({
    entityTags,
    enabled: !!entityTags.length,
  });

  const navigateToAssetOverview = (tag: string) => {
    navigateToPage({ entityTag: tag, source: catalystSource });
  };

  const navigateToKnowledgeGraph = () => {
    navigateToPage({
      entityTag,
      source: catalystSource,
      tab: TabsType.Connections,
      searchParams: getKnowledgeGraphSearchParams({
        entityTag,
        searchParams,
        subtab: KnowledgeGraphTabType.Competitors,
      }),
    });
  };

  const tableColumns: SharedTableColumn<CompetitorTableRow>[] = [
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.ticker'),
      key: 'ticker',
      render: (_, entity) => (
        <div className={styles.assetWrapper}>
          <AssetLogoContainer entity={entity} size={28} />
          <div className={styles.assetTextWrapper}>
            <div className={styles.titleLabel}>
              {entity.name_short || entity.name}
            </div>
            <div className={styles.subtitleLabel}>{formatTicker(entity)}</div>
          </div>
        </div>
      ),
    },
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.change'),
      key: 'priceKey',
      alignment: 'right',
      render: (_, entity) => <ChangeCell entity={entity} />,
    },
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.catalysts1W'),
      key: 'catalystsKey',
      render: (_, entity) => (
        <CompetitorCatalystsCell
          entityTag={entity.tag}
          source={catalystSource}
        />
      ),
    },
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.scenarios1W'),
      key: 'scenariosKey',
      render: (_, entity) => (
        <CompetitorScenariosCell
          entityTag={entity.tag}
          source={catalystSource}
        />
      ),
    },
  ];

  return (
    <TeamsGuard>
      <div className={styles.root}>
        <SectionBase
          title={t('widget:assetOverview.knowledgeGraphRevamp.competitors')}
          isError={(isError && !isCompetitorsNotFound) || isEntitiesError}
          isEmpty={(isSuccess && data.length === 0) || isCompetitorsNotFound}
          emptyTitle={t(
            'widget:assetOverview.knowledgeGraphRevamp.empty.competitors'
          )}
          isFetching={isFetching || isEntitiesLoading}
          onExpand={navigateToKnowledgeGraph}
          onRowClick={(item: SharedTableDataItem<CompetitorTableRow>) =>
            isInDrawer
              ? navigateToAssetOverview(item.tag)
              : openDrawer(item.tag)
          }
          tableData={entitiesData.map(item => ({
            ...item,
            key: item.tag,
          }))}
          tableColumns={tableColumns}
          hideExpandButton={config.app.isTeamsApp}
        />
      </div>
    </TeamsGuard>
  );
};
